// fetch-wrapper.ts
import { setUserToken } from "../../features/userSlice";
import { authService } from "../../logic/authService";
import store from "../../store";
import { navigateTo } from "../../utils/navigation";
import { loginService } from "../login.service";

// ==================== Types ====================
interface IResponse {
  ok: boolean;
  text: () => Promise<string>;
  statusText: string;
  status: number;
}

interface RequestConfig {
  url: string;
  body?: object | FormData;
  oauthTokenHeader?: string;
  skipTokenCheck?: boolean;
}

// ==================== Constants ====================
const TOKEN_REFRESH_BUFFER_MS = 30 * 1000; // 30 seconds before expiry

// ==================== Token Expiration Management ====================
let autoRefreshTimer: NodeJS.Timeout | null = null;
// let visibilityListenerAttached = false;

function isTokenExpiringSoon(): boolean {
  const tokenExpiration = authService.getAccessTokenExpiration();
  if (!tokenExpiration) return false;

  const expirationTime = new Date(tokenExpiration).getTime();
  const currentTime = Date.now();

  return expirationTime - currentTime <= TOKEN_REFRESH_BUFFER_MS;
}

function getTimeUntilRefresh(): number {
  const tokenExpiration = authService.getAccessTokenExpiration();
  if (!tokenExpiration) return -1;

  const expirationTime = new Date(tokenExpiration).getTime();
  const currentTime = Date.now();
  const timeUntilRefresh =
    expirationTime - currentTime - TOKEN_REFRESH_BUFFER_MS;

  return timeUntilRefresh;
}

function clearRefreshTimer(): void {
  if (autoRefreshTimer) {
    clearTimeout(autoRefreshTimer);
    autoRefreshTimer = null;
  }
}

async function handleImmediateRefresh(): Promise<void> {
  try {
    await refreshAccessToken();
    scheduleTokenRefresh();
  } catch (error) {
    console.error("Immediate token refresh failed:", error);
  }
}

function scheduleTokenRefresh(): void {
  clearRefreshTimer();

  const timeUntilRefresh = getTimeUntilRefresh();

  if (timeUntilRefresh < 0) return;

  if (timeUntilRefresh === 0) {
    handleImmediateRefresh();
    return;
  }

  // Schedule refresh for access token (10 minutes - 30 seconds buffer)
  autoRefreshTimer = setTimeout(async () => {
    try {
      await refreshAccessToken();
      scheduleTokenRefresh();
    } catch (error) {
      console.error("Auto token refresh failed:", error);
    }
  }, timeUntilRefresh);
}

// function setupVisibilityListener(): void {
//   if (visibilityListenerAttached || typeof document === "undefined") {
//     return;
//   }

//   document.addEventListener("visibilitychange", () => {
//     if (document.visibilityState === "visible") {
//       // Tab became visible - check if token needs refresh
//       const token = authService.getToken();
//       if (token && isTokenExpiringSoon()) {
//         refreshAccessToken()
//           .then(() => scheduleTokenRefresh())
//           .catch((err) =>
//             console.error("Token refresh on visibility failed:", err)
//           );
//       } else if (token) {
//         // Token still valid but reschedule in case timer was paused
//         scheduleTokenRefresh();
//       }
//     }
//   });

//   visibilityListenerAttached = true;
// }

function initializeAutoRefresh(): void {
  const token = authService.getToken();
  const tokenExpiration = authService.getAccessTokenExpiration();

  if (token && tokenExpiration && getTimeUntilRefresh() >= 0) {
    scheduleTokenRefresh();
  }

  // Set up visibility change listener (only once)
  // setupVisibilityListener();
}

// ==================== Token Refresh Management ====================
let isRefreshInProgress = false;
let refreshingPromise: Promise<void> | null = null;

function updateTokens(data: any): void {
  authService.setToken(data.access_token);
  store.dispatch(setUserToken(data.access_token));
  authService.setAccessTokenExpiration(data.access_token_expires_at);
}

function handleRefreshFailure(): void {
  authService.clearToken();
  navigateTo("/login");
  throw new Error("Refresh failed");
}

async function performRefresh(): Promise<void> {
  try {
    const result = await loginService.refresh();

    if (result?.data?.access_token) {
      updateTokens(result.data);
    } else {
      handleRefreshFailure();
    }
  } catch (error) {
    handleRefreshFailure();
    throw error;
  }
}

async function refreshAccessToken(): Promise<void> {
  if (isRefreshInProgress) {
    return refreshingPromise!;
  }

  isRefreshInProgress = true;
  refreshingPromise = performRefresh();

  try {
    await refreshingPromise;
  } finally {
    isRefreshInProgress = false;
    refreshingPromise = null;
  }
}

async function ensureValidToken(): Promise<void> {
  const token = authService.getToken();

  if (!token) {
    navigateTo("/login");
    throw new Error("No token available");
  }

  initializeAutoRefresh();

  if (isTokenExpiringSoon()) {
    await refreshAccessToken();
    scheduleTokenRefresh();
  }
}

// ==================== Request Builder ====================
function buildHeaders(
  token?: string,
  contentType?: string
): Record<string, string> {
  const headers: Record<string, string> = {};

  if (contentType) {
    headers["Content-Type"] = contentType;
  }

  if (token) {
    headers.Authorization = token;
  }

  return headers;
}

function getAuthToken(
  useRefreshedToken: boolean,
  oauthTokenHeader?: string
): string | undefined {
  if (useRefreshedToken || !oauthTokenHeader) {
    const token = authService.getToken();
    return token ? `Bearer ${token}` : undefined;
  }
  return `Bearer ${oauthTokenHeader}`;
}

function buildRequestOptions(
  method: string,
  token?: string,
  body?: any,
  contentType: string = "application/json"
): RequestInit {
  const options: RequestInit = {
    method,
    headers: buildHeaders(token, contentType),
    credentials: "include",
  };

  if (body) {
    if (body instanceof FormData) {
      options.body = body;
      // Remove Content-Type to let browser set it with boundary
      delete (options.headers as Record<string, string>)["Content-Type"];
    } else if (contentType === "application/x-www-form-urlencoded") {
      options.body = body.toString();
    } else {
      options.body = JSON.stringify(body);
    }
  }

  return options;
}

// ==================== Response Handler ====================
async function handleErrorResponse(
  response: IResponse,
  text: string,
  requestBuilder: (useRefreshedToken?: boolean) => Promise<Response>,
  isRetry: boolean
): Promise<any> {
  // Only retry once on 401 (fallback in case proactive refresh failed)
  if (response.status === 401 && !isRetry) {
    try {
      await refreshAccessToken();
      const retryResponse = await requestBuilder(true);
      return handleResponse(retryResponse as any, requestBuilder, true);
    } catch (e) {
      return Promise.reject({ status: 401, text });
    }
  }

  const errorObject = { response, text };
  return Promise.reject(errorObject);
}

function parseSuccessResponse(response: IResponse, text: string): any {
  try {
    const data = JSON.parse(text);
    return { status: response.status, data };
  } catch {
    return { status: response.status, data: text };
  }
}

async function handleResponse(
  response: IResponse,
  requestBuilder: (useRefreshedToken?: boolean) => Promise<Response>,
  isRetry: boolean = false
): Promise<any> {
  if (response.status === 204) {
    return { status: 204, data: null };
  }

  const text = await response.text().catch(() => "");

  if (!response.ok) {
    return handleErrorResponse(response, text, requestBuilder, isRetry);
  }

  return parseSuccessResponse(response, text);
}

function extractFilename(response: Response): string | undefined {
  const contentDisposition = response.headers.get("Content-Disposition");
  if (!contentDisposition) return undefined;

  const filenameMatch = contentDisposition.match(/filename="?(.+?)"?$/);
  return filenameMatch ? filenameMatch[1] : undefined;
}

async function handleCSVResponse(response: Response): Promise<any> {
  if (!response.ok) {
    const errorData = await response.text().catch(() => "");
    throw new Error(
      `HTTP error! status: ${response.status}, body: ${errorData}`
    );
  }

  const filename = extractFilename(response);
  const blob = await response.blob();

  if (blob.size === 0) {
    throw new Error("Received empty file");
  }

  return { status: response.status, data: blob, filename };
}

// ==================== HTTP Methods ====================
async function prepareRequest(config: RequestConfig): Promise<void> {
  if (!config.oauthTokenHeader && !config.skipTokenCheck) {
    await ensureValidToken();
  }
}

async function get(
  url: string,
  oauthTokenHeader?: string,
  skipTokenCheck?: boolean
): Promise<any> {
  await prepareRequest({ url, oauthTokenHeader, skipTokenCheck });

  const makeRequest = (useRefreshedToken = false) => {
    const token = getAuthToken(useRefreshedToken, oauthTokenHeader);
    const options = buildRequestOptions("GET", token);
    return fetch(url, options);
  };

  return makeRequest().then((res) => handleResponse(res as any, makeRequest));
}

async function post(
  url: string,
  body: object,
  oauthTokenHeader?: string,
  skipTokenCheck?: boolean
): Promise<any> {
  await prepareRequest({ url, body, oauthTokenHeader, skipTokenCheck });

  const makeRequest = (useRefreshedToken = false) => {
    const token = getAuthToken(useRefreshedToken, oauthTokenHeader);
    const options = buildRequestOptions("POST", token, body);
    return fetch(url, options);
  };

  return makeRequest().then((res) => handleResponse(res as any, makeRequest));
}

async function put(
  url: string,
  body: object,
  oauthTokenHeader?: string,
  skipTokenCheck?: boolean
): Promise<any> {
  await prepareRequest({ url, body, oauthTokenHeader, skipTokenCheck });

  const makeRequest = (useRefreshedToken = false) => {
    const token = getAuthToken(useRefreshedToken, oauthTokenHeader);
    const options = buildRequestOptions("PUT", token, body);
    return fetch(url, options);
  };

  return makeRequest().then((res) => handleResponse(res as any, makeRequest));
}

async function patch(
  url: string,
  body: object,
  oauthTokenHeader?: string,
  skipTokenCheck?: boolean
): Promise<any> {
  await prepareRequest({ url, body, oauthTokenHeader, skipTokenCheck });

  const makeRequest = (useRefreshedToken = false) => {
    const token = getAuthToken(useRefreshedToken, oauthTokenHeader);
    const options = buildRequestOptions("PATCH", token, body);
    return fetch(url, options);
  };

  return makeRequest().then((res) => handleResponse(res as any, makeRequest));
}

async function deleteRequest(
  url: string,
  oauthTokenHeader?: string,
  skipTokenCheck?: boolean
): Promise<any> {
  await prepareRequest({ url, oauthTokenHeader, skipTokenCheck });

  const makeRequest = (useRefreshedToken = false) => {
    const token = getAuthToken(useRefreshedToken, oauthTokenHeader);
    const options = buildRequestOptions("DELETE", token);
    return fetch(url, options);
  };

  return makeRequest().then((res) => handleResponse(res as any, makeRequest));
}

async function postForCSV(
  url: string,
  body: any,
  oauthTokenHeader?: string,
  skipTokenCheck?: boolean
): Promise<any> {
  await prepareRequest({ url, body, oauthTokenHeader, skipTokenCheck });

  const makeRequest = (useRefreshedToken = false) => {
    const token = getAuthToken(useRefreshedToken, oauthTokenHeader);
    const options = buildRequestOptions("POST", token, body);
    return fetch(url, options);
  };

  return makeRequest().then((res) => handleResponse(res as any, makeRequest));
}

async function getForCSV(
  url: string,
  oauthTokenHeader?: string,
  skipTokenCheck?: boolean
): Promise<any> {
  await prepareRequest({ url, oauthTokenHeader, skipTokenCheck });

  const token = getAuthToken(false, oauthTokenHeader);
  const options = buildRequestOptions("GET", token);

  return fetch(url, options).then(handleCSVResponse);
}

async function postURLEncoded(
  url: string,
  body: object,
  oauthTokenHeader?: string,
  skipTokenCheck?: boolean
): Promise<any> {
  await prepareRequest({ url, body, oauthTokenHeader, skipTokenCheck });

  const makeRequest = (useRefreshedToken = false) => {
    const token = getAuthToken(useRefreshedToken, oauthTokenHeader);
    const options = buildRequestOptions(
      "POST",
      token,
      body,
      "application/x-www-form-urlencoded"
    );
    return fetch(url, options);
  };

  return makeRequest().then((res) => handleResponse(res as any, makeRequest));
}

function getUrl(url: string): Promise<Response> {
  return fetch(url, { credentials: "include" });
}

// ==================== Public API ====================
export const fetchWrapper = {
  get,
  post,
  postForCSV,
  put,
  patch,
  delete: deleteRequest,
  postURLEncoded,
  getForCSV,
  getUrl,
};

// ==================== Token Management Exports ====================
export const initializeTokenRefresh = initializeAutoRefresh;
export const stopTokenRefresh = clearRefreshTimer;
export { scheduleTokenRefresh };
export const autoInitialize = initializeAutoRefresh;
